import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Leaf, Mail, User, Briefcase } from "lucide-react";

export default function Signup() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { loginWithToken } = useAuth();
  const [role, setRole] = useState(params.get("role") === "provider" ? "provider" : "customer");
  const [form, setForm] = useState({ name: "", email: "", password: "", business_name: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const refFromUrl = params.get("ref") || "";

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/auth/register", { ...form, role, referral_code: refFromUrl || undefined });
      loginWithToken(res.data.token, res.data.user);
      toast.success("Account created!");
      navigate(role === "provider" ? "/provider" : "/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  const googleSignup = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    localStorage.setItem("ll_pending_role", role);
    if (refFromUrl) localStorage.setItem("ll_pending_ref", refFromUrl);
    const redirectUrl = window.location.origin + (role === "provider" ? "/provider" : "/dashboard");
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex items-center justify-center p-6 sm:p-12 order-2 lg:order-1">
        <div className="w-full max-w-sm">
          <Link to="/" className="flex items-center gap-2 mb-8">
            <div className="w-9 h-9 rounded-xl bg-lawn flex items-center justify-center"><Leaf className="w-5 h-5 text-white" /></div>
            <span className="font-display text-xl font-extrabold text-lawn-ink">LawnLink<span className="text-lawn">Pro</span></span>
          </Link>
          <h1 className="font-display text-3xl font-extrabold text-lawn-ink">Create your account</h1>
          <p className="text-muted-foreground mt-1">Join in seconds — it's free.</p>

          <div className="grid grid-cols-2 gap-3 mt-6">
            <button data-testid="role-customer" onClick={() => setRole("customer")} className={`rounded-xl border-2 p-4 text-left transition-colors ${role === "customer" ? "border-lawn bg-lawn-light" : "border-border"}`}>
              <User className={`w-5 h-5 mb-2 ${role === "customer" ? "text-lawn" : "text-muted-foreground"}`} />
              <p className="font-semibold text-sm text-lawn-ink">I'm a Customer</p>
              <p className="text-xs text-muted-foreground">Book lawn care</p>
            </button>
            <button data-testid="role-provider" onClick={() => setRole("provider")} className={`rounded-xl border-2 p-4 text-left transition-colors ${role === "provider" ? "border-lawn bg-lawn-light" : "border-border"}`}>
              <Briefcase className={`w-5 h-5 mb-2 ${role === "provider" ? "text-lawn" : "text-muted-foreground"}`} />
              <p className="font-semibold text-sm text-lawn-ink">I'm a Provider</p>
              <p className="text-xs text-muted-foreground">Grow my business</p>
            </button>
          </div>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label>{role === "provider" ? "Your name" : "Full name"}</Label>
              <Input data-testid="signup-name" value={form.name} onChange={set("name")} required className="mt-1.5 rounded-xl" placeholder="John Smith" />
            </div>
            {role === "provider" && (
              <div>
                <Label>Business name</Label>
                <Input data-testid="signup-business" value={form.business_name} onChange={set("business_name")} className="mt-1.5 rounded-xl" placeholder="GreenBlade Lawn Care" />
              </div>
            )}
            <div>
              <Label>Email</Label>
              <Input data-testid="signup-email" type="email" value={form.email} onChange={set("email")} required className="mt-1.5 rounded-xl" placeholder="you@email.com" />
            </div>
            <div>
              <Label>Password</Label>
              <Input data-testid="signup-password" type="password" value={form.password} onChange={set("password")} required className="mt-1.5 rounded-xl" placeholder="Create a password" />
            </div>
            <Button data-testid="signup-submit" type="submit" disabled={loading} className="w-full rounded-xl bg-lawn hover:bg-lawn-dark font-semibold h-11">{loading ? "Creating…" : `Create ${role} account`}</Button>
          </form>

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-border" /><span className="text-xs text-muted-foreground">OR</span><div className="flex-1 h-px bg-border" />
          </div>
          <Button data-testid="signup-google" onClick={googleSignup} variant="outline" className="w-full rounded-xl h-11 gap-2 font-semibold">
            <Mail className="w-4 h-4" /> Continue with Google
          </Button>
          <p className="text-center text-sm text-muted-foreground mt-6">
            Already have an account? <Link to="/login" className="text-lawn font-semibold hover:underline">Log in</Link>
          </p>
        </div>
      </div>
      <div className="hidden lg:block hero-gradient grain relative order-1 lg:order-2">
        <div className="relative h-full flex flex-col justify-center p-12 text-white">
          <h2 className="font-display text-4xl font-extrabold leading-tight">{role === "provider" ? "Turn your crew into a booked-out business." : "Your yard, handled by trusted local pros."}</h2>
          <ul className="mt-8 space-y-4">
            {(role === "provider"
              ? ["Get discovered by nearby customers", "Premium placement & badges", "AI Success Coach optimizes your profile", "Flexible subscription or commission plans"]
              : ["Compare pros by price & rating", "Book one-time or recurring service", "Secure payments & verified reviews", "Earn rewards by referring friends"]
            ).map((t, i) => (
              <li key={i} className="flex items-center gap-3 text-white/80"><div className="w-6 h-6 rounded-full bg-lawn flex items-center justify-center shrink-0"><Leaf className="w-3.5 h-3.5" /></div>{t}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
